import React, { createContext, useContext, useState, useEffect } from "react";
import { validateCoupon, placeOrder } from "../api/api";
import { API_BASE_URL } from "../api/api";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [tableNo, setTableNo] = useState(
    localStorage.getItem("tableNo") || null,
  );
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [activeOrder, setActiveOrder] = useState(() => {
    return localStorage.getItem("activeOrder") || null;
  });

  useEffect(() => {
    if (activeOrder) {
      localStorage.setItem("activeOrder", activeOrder);
    } else {
      localStorage.removeItem("activeOrder");
    }
  }, [activeOrder]);

  // Load cart from localStorage on mount
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item, quantity = 1, variant = null) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (cartItem) =>
          cartItem.id === item.id && (!variant || cartItem.variant === variant),
      );

      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id && (!variant || cartItem.variant === variant)
            ? { ...cartItem, qty: cartItem.qty + quantity }
            : cartItem,
        );
      }

      return [
        ...prevCart,
        {
          ...item,
          qty: quantity,
          variant: variant,
          totalPrice: (variant?.price || item.price) * quantity,
        },
      ];
    });
  };

  const removeFromCart = (itemId, variant = null) => {
    setCart((prevCart) =>
      prevCart.filter(
        (item) =>
          !(item.id === itemId && (!variant || item.variant === variant)),
      ),
    );
  };

  const updateQuantity = (itemId, quantity, variant = null) => {
    if (quantity <= 0) {
      removeFromCart(itemId, variant);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId && (!variant || item.variant === variant)
          ? { ...item, qty: quantity, totalPrice: item.price * quantity }
          : item,
      ),
    );
  };

  const getItemQuantity = (itemId) => {
    const item = cart.find((item) => item.id === itemId);
    return item ? item.qty : 0;
  };

  const clearCart = () => {
    setCart([]);
    setAppliedCoupon(null);
    setDiscount(0);
  };

  const getCartSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  };

  const getCartTotal = () => {
    const subtotal = getCartSubtotal();
    const delivery = subtotal > 500 ? 0 : 30;
    return subtotal - discount;
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.qty, 0);
  };

  const applyCoupon = async (couponCode) => {
    const subtotal = getCartSubtotal();
    const result = await validateCoupon(couponCode, subtotal);

    if (result.valid) {
      setAppliedCoupon(couponCode);
      setDiscount(result.discount_amount || 0);
      return {
        success: true,
        message: `Coupon applied! You saved ₹${result.discount_amount}`,
      };
    }

    return { success: false, message: result.message || "Invalid coupon" };
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
  };

  // submitOrder
  const submitOrder = async () => {
    try {
      console.log("CURRENT TABLE:", tableNo);
      const orderData = {
        order_type: "dine_in",
        table_id: tableNo || null,

        items: cart.map((item) => ({
          menu_item_id: item.id,

          name: item.item_name,

          variant:
            typeof item.variant === "object"
              ? item.variant?.name
              : item.variant || null,

          price: item.price,

          qty: item.qty,
        })),

        subtotal: getCartSubtotal(),

        tax: 0,

        service_charge: 0,

        discount: discount || 0,

        total: getCartTotal(),

        payment_status: "pending",

        payment_method: "cash",
      };

      console.log("SENDING ORDER:", orderData);
      console.log("SENDING ORDER:", orderData);

      const data = await placeOrder(orderData);

      console.log("ORDER RESPONSE:", data);

      if (data.success) {
        clearCart();

        const orderId = data.order_id;

        setActiveOrder(orderId);
        localStorage.setItem("activeOrder", orderId);

        setCart([]);

        return {
          success: true,
          orderId: data.order_id,
        };
      }

      return {
        success: false,
        message: data.message || "Order failed",
      };
    } catch (error) {
      console.error("ORDER ERROR:", error);

      return {
        success: false,
        message: "Server error",
      };
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        getItemQuantity,
        clearCart,
        getCartSubtotal,
        getCartTotal,
        getCartCount,
        tableNo,
        setTableNo,
        appliedCoupon,
        discount,
        applyCoupon,
        removeCoupon,
        submitOrder,
        activeOrder,
        setActiveOrder,
      }}>
      {children}
    </CartContext.Provider>
  );
};
